/**
 * Swift method generator - Functional implementation with dependency injection
 * Generates Swift method implementations for BrickModule extensions
 */

import type { ModuleConstant, ModuleMethod, ParsedModule } from "../../parser";
import { isAnyObjectType, isAnyType } from "../../utils/type-helpers";
import type { SwiftTypeMapper } from "./swift-type-mapper";

export interface SwiftMethodGeneratorDeps {
  typeMapper: SwiftTypeMapper;
}

export interface SwiftMethodGenerator {
  /**
   * Generates a Swift module method implementation for BrickModule extension
   */
  generateSwiftModuleMethod(method: ModuleMethod, module: ParsedModule): string;

  /**
   * Generates a constant getter implementation
   */
  generateModuleConstantGetter(constant: ModuleConstant, moduleName: string): string;
}

/**
 * Creates a Swift method generator with injected dependencies
 */
export function createSwiftMethodGenerator(
  deps: SwiftMethodGeneratorDeps,
): SwiftMethodGenerator {
  const { typeMapper } = deps;

  /**
   * Generates a Swift module method implementation for BrickModule extension
   */
  function generateSwiftModuleMethod(
    method: ModuleMethod,
    module: ParsedModule,
  ): string {
    const moduleName = module.moduleName;
    const fullMethodName = `${moduleName}_${method.name}`;
    const swiftParams = mapParamsToSwift(method.params);
    const swiftReturnType = mapReturnTypeToSwift(method.returnType);

    if (method.isAsync) {
      return generateAsyncMethod(
        method,
        module,
        fullMethodName,
        swiftParams,
        swiftReturnType,
      );
    }
    return generateSyncMethod(
      method,
      module,
      fullMethodName,
      swiftParams,
      swiftReturnType,
    );
  }

  /**
   * Generates an async Swift method implementation
   */
  function generateAsyncMethod(
    method: ModuleMethod,
    module: ParsedModule,
    fullMethodName: string,
    swiftParams: Array<{ name: string; type: string }>,
    _swiftReturnType: string,
  ): string {
    const moduleName = module.moduleName;
    const paramList = swiftParams
      .map((p) => `${p.name}: ${p.type}`)
      .concat([
        "resolve: @escaping RCTPromiseResolveBlock",
        "reject: @escaping RCTPromiseRejectBlock",
      ])
      .join(", ");

    const methodParams = generateMethodCallParams(method, module, true);

    // Check if return type is a complex type that needs conversion
    const returnType = method.returnType
      .replace("Promise<", "")
      .replace(">", "");
    const isComplexReturnType =
      !["string", "number", "boolean", "void"].includes(returnType) &&
      !returnType.includes("[]") &&
      !isAnyObjectType(returnType) && // AnyObject maps to [String: Any], no conversion needed
      !isAnyType(returnType); // Any can be any value type, no conversion needed

    const resultHandling = isComplexReturnType
      ? "resolve(try self.convertToDict(result))"
      : "resolve(result)";

    return `@objc public func ${fullMethodName}(${paramList}) -> Void {
        Task {
            do {
                // Get the module instance
                guard let module = brickModuleRegistry?.getModuleInstance("${moduleName}") else {
                    let error = NSError(domain: "BrickModule", code: 404, userInfo: [
                        NSLocalizedDescriptionKey: "Module '${moduleName}' not found"
                    ])
                    reject("MODULE_NOT_FOUND", "Module not found", error)
                    return
                }

                // Cast to the expected protocol and call the method
                guard let typedModule = module as? ${moduleName}Spec else {
                    let error = NSError(domain: "BrickModule", code: 500, userInfo: [
                        NSLocalizedDescriptionKey: "Module does not conform to expected protocol"
                    ])
                    reject("TYPE_ERROR", "Protocol mismatch", error)
                    return
                }

                let result = try await typedModule.${method.name}(${methodParams})
                ${resultHandling}
            } catch {
                reject("EXECUTION_ERROR", error.localizedDescription, error)
            }        }
    }`;
  }

  /**
   * Generates a sync Swift method implementation
   */
  function generateSyncMethod(
    method: ModuleMethod,
    module: ParsedModule,
    fullMethodName: string,
    swiftParams: Array<{ name: string; type: string }>,
    swiftReturnType: string,
  ): string {
    const moduleName = module.moduleName;
    const paramList = swiftParams.map((p) => `${p.name}: ${p.type}`).join(", ");

    const methodParams = generateMethodCallParams(method, module, false);

    // Swift error handling for Objective-C interop:
    // We add an NSErrorPointer parameter so Objective-C can receive Swift errors
    // This is the standard way to expose throwing Swift methods to Objective-C

    // For void methods
    if (swiftReturnType === "Void") {
      const errorParam = paramList ? ", error: NSErrorPointer" : "error: NSErrorPointer";
      return `@objc public func ${fullMethodName}(${paramList}${errorParam}) -> Bool {
        do {
            // Get the module instance
            guard let module = brickModuleRegistry?.getModuleInstance("${moduleName}") else {
                let err = NSError(domain: "BrickModule", code: 404, userInfo: [NSLocalizedDescriptionKey: "Module '${moduleName}' not found"])
                error?.pointee = err
                return false
            }

            // Cast to the expected protocol and call the method
            guard let typedModule = module as? ${moduleName}Spec else {
                let err = NSError(domain: "BrickModule", code: 500, userInfo: [NSLocalizedDescriptionKey: "Module does not conform to ${moduleName}Spec"])
                error?.pointee = err
                return false
            }

            try typedModule.${method.name}(${methodParams})
            return true
        } catch let err {
            error?.pointee = err as NSError
            return false
        }
    }`;
    }

    // Non-void sync return handling
    const isComplexReturnType =
      !["string", "number", "boolean", "void"].includes(method.returnType) &&
      !method.returnType.includes("[]") &&
      !isAnyObjectType(method.returnType) && // AnyObject maps to [String: Any], no conversion needed
      !isAnyType(method.returnType); // Any can be any value type, no conversion needed

    const isBooleanReturnType = method.returnType === "boolean";

    // For non-void methods, we also use NSErrorPointer pattern
    // Return nil on error, actual value on success
    // Use 'Any?' (id in Objective-C) as return type to support all types

    let resultHandling: string;
    if (isComplexReturnType) {
      resultHandling = `try self.convertToDict(try typedModule.${method.name}(${methodParams}))`;
    } else if (isBooleanReturnType) {
      // Convert Bool to NSNumber for Objective-C compatibility
      resultHandling = `NSNumber(value: try typedModule.${method.name}(${methodParams}))`;
    } else if (method.returnType === "number") {
      // Wrap number in NSNumber for consistent handling
      resultHandling = `NSNumber(value: try typedModule.${method.name}(${methodParams}))`;
    } else {
      // String and complex objects can be returned directly
      resultHandling = `try typedModule.${method.name}(${methodParams})`;
    }

    const errorParam = paramList ? ", error: NSErrorPointer" : "error: NSErrorPointer";

    // Return 'Any?' which maps to 'id' in Objective-C (can represent any object type)
    return `@objc public func ${fullMethodName}(${paramList}${errorParam}) -> Any? {
        do {
            // Get the module instance
            guard let module = brickModuleRegistry?.getModuleInstance("${moduleName}") else {
                let err = NSError(domain: "BrickModule", code: 404, userInfo: [NSLocalizedDescriptionKey: "Module '${moduleName}' not found"])
                error?.pointee = err
                return nil
            }

            // Cast to the expected protocol and call the method
            guard let typedModule = module as? ${moduleName}Spec else {
                let err = NSError(domain: "BrickModule", code: 500, userInfo: [NSLocalizedDescriptionKey: "Module does not conform to ${moduleName}Spec"])
                error?.pointee = err
                return nil
            }

            return ${resultHandling}
        } catch let err {
            error?.pointee = err as NSError
            return nil
        }
    }`;
  }

  /**
   * Generates method call parameters with conversion logic
   */
  function generateMethodCallParams(
    method: ModuleMethod,
    module: ParsedModule,
    _isAsync: boolean,
  ): string {
    const moduleName = module.moduleName;
    const ifaceNames = new Set(
      (module.interfaceDefinitions || []).map((i) => i.name),
    );

    return method.params
      .map((p) => {
        // Check if this is a known interface type (not a primitive)
        const isComplexType =
          !["string", "number", "boolean", "void"].includes(p.type) &&
          !p.type.includes("[]") &&
          !p.type.startsWith("Promise");
        const isArrayOfComplex =
          p.type.endsWith("[]") &&
          !["string[]", "number[]", "boolean[]"].includes(p.type);

        // Wrap callback/function parameters (bridge block -> protocol closure)
        const cbInfo = p.functionType;
        if (cbInfo) {
          return generateCallbackParameter(
            p.name,
            cbInfo,
            moduleName,
            ifaceNames,
          );
        }

        // For '{}' or 'any' or 'AnyObject' or 'Any', do not attempt Codable conversion; pass through as-is
        const t = (p.type || "").trim();
        if (isAnyObjectType(t) || isAnyType(t)) {
          return `${p.name}: ${p.name}`;
        }

        if (isComplexType) {
          // It's an interface type like 'User', 'TwoDepthObj', etc.
          const interfaceName = p.type; // The type is already the interface name
          return `${p.name}: try self.convertFromDict(${p.name}, to: ${moduleName}${interfaceName}.self)`;
        }
        if (isArrayOfComplex) {
          // Array of interface types like 'User[]'
          const elementType = p.type.slice(0, -2); // Remove '[]'
          return `${p.name}: try ${p.name}.map { try self.convertFromDict($0 as! [String: Any], to: ${moduleName}${elementType}.self) }`;
        }
        return `${p.name}: ${p.name}`;
      })
      .join(", ");
  }

  /**
   * Generates callback parameter with proper conversion
   */
  function generateCallbackParameter(
    paramName: string,
    cbInfo: any,
    _moduleName: string,
    ifaceNames: Set<string>,
  ): string {
    const argNames = (cbInfo.paramTypes || []).map(
      (_: any, idx: number) => `v${idx}`,
    );
    const argList = argNames.join(", ");

    if (argNames.length === 0) {
      return `${paramName}: { ${paramName}([]) }`;
    }

    const isSingleRestArray =
      argNames.length === 1 && cbInfo.paramRestFlags?.[0] === true;

    if (isSingleRestArray) {
      // Forward the array as-is to RCTResponseSenderBlock to represent spread args
      return `${paramName}: { v0 in ${paramName}(v0) }`;
    }

    // Map each callback argument into a JSON-compatible value
    const convertedArgs = (cbInfo.paramTypes || []).map(
      (t: string, idx: number) => {
        const tt = (t || "").trim();
        const v = `v${idx}`;

        // Inline object or AnyObject/any -> already [String: Any]
        if (isAnyObjectType(tt) || (tt.startsWith("{") && tt.endsWith("}"))) {
          return v;
        }

        // Array types
        if (tt.endsWith("[]")) {
          const elem = tt.slice(0, -2).trim();
          if (ifaceNames.has(elem)) {
            // Array of interface objects -> map each to dict (non-throwing)
            return `${v}.map { (try? self.convertToDict($0)) ?? [:] }`;
          }
          // Primitives arrays or other -> pass through
          return v;
        }

        // Interface type -> convert to dictionary (non-throwing)
        if (ifaceNames.has(tt)) {
          return `(try? self.convertToDict(${v})) ?? [:]`;
        }

        // Primitive
        return v;
      },
    );

    const bodyArray = convertedArgs.join(", ");
    return `${paramName}: { ${argList} in ${paramName}([${bodyArray}]) }`;
  }

  /**
   * Generates a constant getter implementation
   */
  function generateModuleConstantGetter(
    constant: ModuleConstant,
    moduleName: string,
  ): string {
    const fullMethodName = `${moduleName}_${constant.name}`;
    const swiftType = typeMapper.mapTypeScriptToSwiftForObjC(constant.type);
    const defaultValue = typeMapper.getSwiftDefaultValue(swiftType);

    return `@objc public func ${fullMethodName}() -> ${swiftType} {
        do {
            // Get the module instance
            guard let module = brickModuleRegistry?.getModuleInstance("${moduleName}") else {
                return ${defaultValue}
            }

            // Cast to the expected protocol and get the constant
            if let typedModule = module as? ${moduleName}Spec {
                return typedModule.${constant.name}
            } else {
                return ${defaultValue}
            }
        } catch {
            return ${defaultValue}
        }
    }`;
  }

  /**
   * Helper method to map params to Swift types
   */
  function mapParamsToSwift(
    params: Array<{ name: string; type: string; isFunction?: boolean }>,
  ): Array<{ name: string; type: string }> {
    return params.map((param) => ({
      name: param.name,
      type: param.isFunction
        ? "@escaping RCTResponseSenderBlock"
        : typeMapper.mapTypeScriptToSwiftForObjC(param.type),
    }));
  }

  /**
   * Helper method to map return type to Swift
   */
  function mapReturnTypeToSwift(returnType: string): string {
    if (returnType.startsWith("Promise<") && returnType.endsWith(">")) {
      return "Void"; // Async methods return Void in Objective-C bridge
    }
    return typeMapper.mapTypeScriptToSwiftForObjC(returnType);
  }

  return {
    generateSwiftModuleMethod,
    generateModuleConstantGetter,
  };
}
